import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useMotionValue, useSpring, useMotionTemplate, useReducedMotion } from 'framer-motion';

/**
 * A card that lights up under the cursor instead of tilting.
 *
 * Two layers do the work. A warm pool of light is painted *behind* the card —
 * the glass surface is translucent and carries a backdrop blur, so the light
 * diffuses up through it rather than washing over the text. A second layer on
 * top paints only the 1px border, brightening the gold edge nearest the
 * cursor. Nothing rotates, so tall cards never read as wobbly.
 *
 * Children keep their own rounding; pass the matching `rounded-*` class here
 * so the overlays inherit the same radius.
 */
export default function SpotlightCard({
  children,
  className = '',
  glow = 280,
  lift = 4,
  ...rest
}) {
  const ref = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    const fine = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false;
    setEnabled(fine && !reduced);
  }, [reduced]);

  // Position follows the cursor exactly — a spring here reads as lag, not polish.
  const x = useMotionValue(-9999);
  const y = useMotionValue(-9999);
  // Only the fade in and out is eased.
  const presence = useMotionValue(0);
  const opacity = useSpring(presence, { stiffness: 190, damping: 28, mass: 0.4 });

  const surface = useMotionTemplate`radial-gradient(${glow}px circle at ${x}px ${y}px, rgba(255, 235, 193, 0.9), rgba(240, 169, 182, 0.5) 40%, rgba(240, 169, 182, 0) 72%)`;
  const edge = useMotionTemplate`radial-gradient(${glow * 0.85}px circle at ${x}px ${y}px, rgba(255, 247, 226, 1), rgba(201, 165, 74, 0.8) 42%, rgba(201, 165, 74, 0) 76%)`;

  const track = useCallback(
    (e) => {
      if (!enabled || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      x.set(e.clientX - rect.left);
      y.set(e.clientY - rect.top);
    },
    [enabled, x, y]
  );

  const handleEnter = useCallback(
    (e) => {
      if (!enabled) return;
      track(e);
      presence.set(1);
    },
    [enabled, track, presence]
  );

  const handleLeave = useCallback(() => presence.set(0), [presence]);

  return (
    <motion.div
      ref={ref}
      onPointerMove={track}
      onPointerEnter={handleEnter}
      onPointerLeave={handleLeave}
      whileHover={enabled ? { y: -lift } : undefined}
      transition={{ type: 'spring', stiffness: 300, damping: 26, mass: 0.5 }}
      className={`relative ${className}`}
      {...rest}
    >
      {/* behind the glass — diffuses up through it */}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{ background: surface, opacity }}
      />

      {children}

      {/* on top, but masked to the 1px border only */}
      <motion.span
        aria-hidden
        className="edge-mask pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{ background: edge, opacity }}
      />
    </motion.div>
  );
}
